import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Input, Select, Tag } from "antd";
import { DownOutlined, FilterOutlined, SearchOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { AuthStorage } from "utils/auth-storage";
import { ProjectServices } from "apis/Services/ProjectService";
import ProjectTable from "components/projects-table";
import { projectDetailsToDefault } from "redux/project-reducer";
import { filterProjects, getFilterOptions, PROGRESS_OPTIONS } from "./project-utils";
import styles from "./project.module.css";

const normalizeCompanyId = (value) => {
  if (value === null || value === undefined || value === "") return undefined;
  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? value : numericValue;
};

const getCompanyOptions = () => {
  try {
    const companies = JSON.parse(AuthStorage.getItem("companies_object") || "{}");
    if (!companies || typeof companies !== "object" || Array.isArray(companies)) {
      return [];
    }
    return Object.entries(companies)
      .map(([label, id]) => ({ label, value: normalizeCompanyId(id) }))
      .filter(({ value }) => value !== undefined);
  } catch (error) {
    console.error("Unable to read the cached company list", error);
    return [];
  }
};

const Projects = () => {
  const { company_id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [projects, setProjects] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [loadVersion, setLoadVersion] = useState(0);
  const [search, setSearch] = useState("");
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [progressFilter, setProgressFilter] = useState(null);
  const [typeFilter, setTypeFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const companyOptions = useMemo(getCompanyOptions, [company_id]);
  const selectedCompany = companyOptions.find(
    ({ value }) => String(value) === String(company_id),
  );

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    setLoadError("");
    setProjects([]);
    ProjectServices.getProjects({
      company_id,
      code: AuthStorage.getItem("token"),
      loginEmail: AuthStorage.getItem("email"),
    })
      .then((response) => {
        if (!active) return;
        setProjects(
          Array.isArray(response?.content)
            ? response.content.filter((project) => project && typeof project === "object")
            : [],
        );
      })
      .catch(() => {
        if (active) setLoadError("Unable to load projects. Please try again.");
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [company_id, loadVersion]);

  useEffect(() => {
    dispatch(projectDetailsToDefault());
  }, [dispatch]);

  const typeOptions = useMemo(() => getFilterOptions(projects, "Type"), [projects]);
  const statusOptions = useMemo(
    () => getFilterOptions(projects, "RegistrationStatus"),
    [projects],
  );
  const filteredProjects = useMemo(
    () => filterProjects(projects, { search, progressFilter, typeFilter, statusFilter }),
    [projects, search, progressFilter, typeFilter, statusFilter],
  );
  const activeFilters = [
    {
      key: "progress",
      label: "Progress",
      value: progressFilter,
      text: PROGRESS_OPTIONS.find(({ value }) => value === progressFilter)?.label,
      clear: () => setProgressFilter(null),
    },
    { key: "type", label: "Type", value: typeFilter, text: typeFilter, clear: () => setTypeFilter(null) },
    { key: "status", label: "Status", value: statusFilter, text: statusFilter, clear: () => setStatusFilter(null) },
  ].filter(({ value }) => value);
  const hasSearchOrFilters = Boolean(search.trim() || activeFilters.length);

  const resetFilters = () => {
    setProgressFilter(null);
    setTypeFilter(null);
    setStatusFilter(null);
  };
  const clearAll = () => {
    resetFilters();
    setSearch("");
  };
  const handleCompanySelect = (value) => {
    if (String(value) === String(company_id)) return;
    AuthStorage.setItem("company_id", value);
    navigate(`/main/company/${value}/projects`);
    // Refresh the company context used by the navbar and other company pages.
    window.setTimeout(() => window.location.reload(), 200);
  };

  return (
    <main className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}>Projects</h1>
        {/* <div className={styles.companyField}>
          <label htmlFor="projects-company">Company</label>
          <Select
            id="projects-company"
            aria-label="Company"
            className={styles.companySelect}
            options={companyOptions}
            value={selectedCompany?.value ?? null}
            onChange={handleCompanySelect}
            disabled={!companyOptions.length}
            placeholder={companyOptions.length ? "Select a company" : "Companies unavailable"}
            notFoundContent="No companies available"
            showSearch
            optionFilterProp="label"
          />
        </div> */}
      </div>

      <section className={styles.panel} aria-label="Project list">
        <div className={styles.toolbar}>
          <div className={styles.searchControls}>
            <Input
              className={styles.search}
              prefix={<SearchOutlined />}
              placeholder="Search by project name, number or type"
              aria-label="Search projects"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              allowClear
            />
            <Button
              className={`${styles.filterToggle} ${filtersExpanded || activeFilters.length ? styles.filterToggleActive : ""}`}
              icon={<FilterOutlined />}
              aria-expanded={filtersExpanded}
              aria-controls="project-filter-options"
              onClick={() => setFiltersExpanded((expanded) => !expanded)}
            >
              Filters{activeFilters.length > 0 ? ` (${activeFilters.length})` : ""}
              <DownOutlined className={`${styles.filterChevron} ${filtersExpanded ? styles.filterChevronExpanded : ""}`} />
            </Button>
          </div>
          <span className={styles.resultCount} role="status">
            {isLoading ? "Loading projects…" : loadError ? "Projects unavailable" : (
              <><strong>{filteredProjects.length}</strong>{hasSearchOrFilters ? ` of ${projects.length}` : ""} {projects.length === 1 ? "project" : "projects"}{hasSearchOrFilters ? " shown" : " available"}</>
            )}
          </span>
        </div>

        <div
          id="project-filter-options"
          className={styles.filters}
          aria-label="Project filters"
          hidden={!filtersExpanded}
        >
          <div className={styles.filterField}>
            <label htmlFor="projects-progress">Progress</label>
            <Select
              id="projects-progress"
              aria-label="Project progress"
              placeholder="All progress"
              options={PROGRESS_OPTIONS}
              value={progressFilter}
              onChange={(value) => setProgressFilter(value ?? null)}
              disabled={isLoading || Boolean(loadError)}
              allowClear
            />
          </div>
          <div className={styles.filterField}>
            <label htmlFor="projects-type">Project type</label>
            <Select
              id="projects-type"
              aria-label="Project type"
              placeholder="All types"
              options={typeOptions}
              value={typeFilter}
              onChange={(value) => setTypeFilter(value ?? null)}
              disabled={isLoading || !typeOptions.length}
              showSearch
              optionFilterProp="label"
              allowClear
            />
          </div>
          <div className={styles.filterField}>
            <label htmlFor="projects-status">Registration status</label>
            <Select
              id="projects-status"
              aria-label="Registration status"
              placeholder="All statuses"
              options={statusOptions}
              value={statusFilter}
              onChange={(value) => setStatusFilter(value ?? null)}
              disabled={isLoading || !statusOptions.length}
              allowClear
            />
          </div>
          {activeFilters.length > 0 && <Button type="link" className={styles.clearFilters} onClick={resetFilters}>Clear filters</Button>}
        </div>

        {activeFilters.length > 0 && (
          <div className={styles.activeFilters} aria-label="Active filters">
            {activeFilters.map(({ key, label, text, clear }) => (
              <Tag key={key} closable onClose={clear}>{label}: {text}</Tag>
            ))}
          </div>
        )}

        {loadError ? (
          <div className={styles.errorState}>
            <Alert type="error" showIcon message={loadError} action={<Button onClick={() => setLoadVersion((version) => version + 1)}>Retry</Button>} />
          </div>
        ) : (
          <ProjectTable
            data={filteredProjects}
            companyId={company_id}
            loading={isLoading}
            isFiltered={hasSearchOrFilters}
            onClearFilters={clearAll}
          />
        )}
      </section>
    </main>
  );
};

export default Projects;
